import { isImportWarning } from '@dcp/shared'
import type { FileImportRow } from '@/types/banking'

/** One line of an import's issue list. `row` is 0 for file-level entries. */
export interface ImportIssue {
  row: number
  message: string
}

/** Longest issue list rendered for one import before it is summarised. */
export const MAX_LISTED_ISSUES = 50

/**
 * Issues worth showing the user, i.e. everything except catalog-level notes.
 *
 * `file_imports.errors` is one JSONB array with no severity field, so blocking
 * row errors, non-fatal notes about the uploaded file and notes about the
 * TENANT'S CATALOG all land in it together. Only the last group carries the
 * `WARNING:` prefix (`isImportWarning`), and it is the group DCP-359 is about:
 * a successful 581-of-581 import must not announce "4 errors" because the
 * tenant happens to own duplicate bank accounts. Those notes stay persisted and
 * logged for support; they are just not the user's problem to look at.
 *
 * Everything unprefixed is kept — including notes that are not failures, like
 * `Category not found: "X"` on rows that did import — which is why the list is
 * offered on completed imports too and is called "issues", not "errors".
 *
 * @param entries - `errors` payload off the file_imports row, if any
 * @returns Displayable entries, defensively normalised (the JSONB has been
 *   written by several releases; a non-string message must not throw mid-render)
 */
export function listableIssues(entries: FileImportRow['errors'] | undefined): ImportIssue[] {
  const issues: ImportIssue[] = []
  for (const entry of entries ?? []) {
    if (typeof entry?.message !== 'string') continue
    if (isImportWarning(entry.message)) continue
    issues.push({ row: typeof entry.row === 'number' ? entry.row : 0, message: entry.message })
  }
  return issues
}

/**
 * "1 issue", "3 issues" — the count shown in the Import History Issues column.
 *
 * @param count - Number of listable issues
 */
export function issueCountLabel(count: number): string {
  return `${count} issue${count === 1 ? '' : 's'}`
}
